
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { createProvider } from 'react-redux';
import { Fragment } from '../../utilities/components/Fragment';
import { IItemCacheConfiguration, IStore, createItemCacheStore } from '../../store/createStore';
import { IItemCacheState } from '../../store/reducers/itemCacheReducer';
import {
  BackgroundTaskBuffer,
  BackgroundTaskHost,
  BackgroundTaskContextProvider,
  IBackgroundTaskContext,
  BACKGROUND_TASK_CONTEXT_TYPES
} from '../backgroundTaskRunner/BackgroundTaskHost';
import { backgroundTaskQueueFacet, configurationItemKey } from '../../facets/ConfigurationFacets';
import { IBackgroundTaskQueue, IBackgroundTask } from '../../models/backgroundTasks/IBackgroundTaskQueue';
import { readItems } from '../../operations/readItems';
import { IItemCache } from '../../models/IItemCacheObserver';

/**
 * @public
 */
export interface IItemCacheProviderProps {
  configuration?: IItemCacheConfiguration;
  store?: IStore<IItemCacheState>;
}

/**
 * @internal
 */
export const ITEM_CACHE_STORE_KEY = 'itemCacheStore';

const Provider = createProvider(ITEM_CACHE_STORE_KEY);

const ITEM_CACHE_STORE_CONTEXT_TYPES: PropTypes.ValidationMap<IItemCacheProviderContext> = {
  itemCacheStore: PropTypes.object
};

/**
 * @internal
 */
export interface IItemCacheProviderContext {
  itemCacheStore?: IStore<IItemCacheState>;
}

/**
 * Component which provides a context containing an item cache.
 * The item cache can be consumed in child components by using the `connectToItemCache` function.
 * @public
 */
export class ItemCacheProvider extends React.PureComponent<IItemCacheProviderProps, {}> {
  public static readonly contextTypes: PropTypes.ValidationMap<IItemCacheProviderContext & IBackgroundTaskContext> = {
    ...ITEM_CACHE_STORE_CONTEXT_TYPES,
    ...BACKGROUND_TASK_CONTEXT_TYPES
  };

  public context!: IItemCacheProviderContext & IBackgroundTaskContext;

  private _store: IStore<IItemCacheState>;
  private _ownStore: {
    dispose(): void;
  } | undefined;

  private _backgroundTaskQueue: IBackgroundTaskQueue;
  private _ownBackgroundTaskQueue: BackgroundTaskBuffer | undefined;

  constructor(props: IItemCacheProviderProps, context: IItemCacheProviderContext) {
    super(props, context);

    if (props.store && props.configuration) {
      throw new Error(`Cannot initialize an ItemCacheProvider with both 'store' and 'configuration' props.`);
    }

    if (props.store) {
      this._store = props.store;
    } else {
      if (!this.context.itemCacheStore) {
        this._store = this._ownStore = createItemCacheStore({
          configuration: props.configuration
        });
      } else {
        this._store = this.context.itemCacheStore;
      }
    }

    const backgroundTaskQueue =
      (!props.store ? this.context.backgroundTaskQueue : undefined) ||
      this._store.dispatch(readItems((itemCache: IItemCache) => {
        return itemCache.demandItemFacet(backgroundTaskQueueFacet, configurationItemKey);
      })) ||
      (this._ownBackgroundTaskQueue = new BackgroundTaskBuffer());

    this._backgroundTaskQueue = {
      enqueue: (task: IBackgroundTask) => {
        backgroundTaskQueue.enqueue({
          content: (
            <Provider store={ this._store }>
              <BackgroundTaskContextProvider queue={ this._backgroundTaskQueue }>
                { task.content }
              </BackgroundTaskContextProvider>
            </Provider>
          ),
          promise: task.promise
        });
      }
    };
  }

  public render(): JSX.Element {
    return (
      <Fragment>
        {
          this._ownBackgroundTaskQueue ? (
            <BackgroundTaskHost queueRef={ this._ownBackgroundTaskQueue.queueRef } />
          ) : null
        }
        <Provider store={ this._store }>
          <BackgroundTaskContextProvider queue={ this._backgroundTaskQueue }>
            { this.props.children }
          </BackgroundTaskContextProvider>
        </Provider>
      </Fragment>
    );
  }

  public componentWillUnmount(): void {
    const ownStore = this._ownStore;

    if (ownStore) {
      ownStore.dispose();
    }
  }
}
